import {
  FieldError,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from "react-hook-form";
import { toast } from "sonner";

import { ErrorMessages } from "@/api/fetcher_v2";

// Our field level server errors are always either a string or an array of strings
const isError = (v: unknown | unknown[]) =>
  typeof v === "string" ||
  (Array.isArray(v) && v.every((iv) => typeof iv === "string"));

const makeErrorFromServerError = (message: string | string[] | unknown) => ({
  type: "400",
  message: Array.isArray(message)
    ? message.join(", ")
    : typeof message === "string"
      ? message
      : undefined,
});

/*
 * This function looks pretty complex, but basically
 * it takes a validation error object and transforms it into a flat object
 * that is easily usable by react-hook-form.
 */

export function serverErrorForHookForm<T extends FieldValues>(
  errors: ErrorMessages<T>
) {
  const transformedErrors: [FieldPath<T>, FieldError][] = [];

  // declare a transform that can be called recursively
  // Path must always end with a dot OR be empty when passed to the nested transformers.
  const transformer = (_errors: unknown, path = "") => {
    // The value is an array and needs to be traversed
    if (Array.isArray(_errors) && !isError(_errors)) {
      _errors.forEach((v, i) => {
        if (typeof v === "object" && !isError(v)) {
          transformer(v, `${path}[${i}].`);
        }
        if (isError(v)) {
          transformedErrors.push([
            `${path}[${i}]` as FieldPath<T>,
            makeErrorFromServerError(v),
          ]);
        }
      });
    }

    // The value is an object and needs to be traversed
    if (typeof _errors === "object" && !Array.isArray(_errors)) {
      Object.entries(_errors as object).forEach(([key, value]) => {
        if (typeof value === "object" && !isError(value)) {
          transformer(value, `${path}${key}.`);
        }
        // The value is an object and needs to be traversed
        if (isError(value)) {
          transformedErrors.push([
            `${path}${key}` as FieldPath<T>,
            makeErrorFromServerError(value),
          ]);
        }
      });
    }
  };

  transformer(errors);

  return transformedErrors;
}

/**
 * Applies server validation errors to a react-hook-form instance.
 * Field errors are set inline; unrecognized errors are shown as a toast.
 */
export function applyServerErrors<T extends FieldValues>(
  data: ErrorMessages<T>,
  form: Pick<UseFormReturn<T>, "control" | "setError">,
  apiToFormField: Record<string, FieldPath<T>> = {}
) {
  const formFields = new Set<string>(Object.keys(form.control._defaultValues));
  const errors = serverErrorForHookForm(data);
  const unhandledErrors: string[] = [];

  for (const [fieldPath, error] of errors) {
    const mappedField = apiToFormField[fieldPath] ?? fieldPath;
    if (formFields.has(mappedField) && error.message) {
      form.setError(mappedField as FieldPath<T>, error);
    } else if (error.message) {
      unhandledErrors.push(error.message);
    }
  }

  if (unhandledErrors.length > 0) {
    toast.error(unhandledErrors.join(". "));
  }
}
